import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Zap, Building2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SERVICE_AREA } from "@/data/serviceArea";
import { CITY_LANDING_PAGES } from "@/data/cityLandingPages";

// Map city names to their landing page URLs
const getCityUrl = (cityName: string): string | null => {
  const cityConfig = Object.values(CITY_LANDING_PAGES).find(
    city => city.name === cityName
  );
  return cityConfig?.urlPath || null;
};

const RegionalServiceBanner = () => {
  return (
    <section 
      className="py-12 md:py-16 bg-gradient-to-br from-amber-50 via-yellow-50/80 to-orange-50/50 dark:from-amber-950/30 dark:via-yellow-950/20 dark:to-orange-950/10 border-y border-amber-200/50 dark:border-amber-800/30"
      aria-labelledby="regional-service-heading"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <header className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 text-primary font-semibold text-sm mb-3">
              <Zap className="w-4 h-4" />
              HLAVNÍ OBLAST PŮSOBNOSTI
            </span>
            <h2 
              id="regional-service-heading" 
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3"
            >
              Elektrikář <span className="text-primary">Teplice</span> a okolí
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Jsme lokální firma se sídlem v Teplicích. Pokrýváme celý Ústecký kraj s dojezdem do 50 km.
            </p>
          </header>

          {/* Main Center Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary to-amber-400 rounded-xl shadow-lg shadow-primary/30">
              <Building2 className="w-6 h-6 text-slate-900" />
              <div className="text-left">
                <span className="block text-xs text-slate-700 font-medium">Sídlo firmy</span>
                <span className="block text-lg font-bold text-slate-900">{SERVICE_AREA.center.name}</span>
              </div>
            </div>
          </div>

          {/* District Cities Grid - with clickable links */}
          <div className="mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Okresní města v dosahu</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {SERVICE_AREA.mainCities.map((city) => {
                const cityUrl = getCityUrl(city.name);
                
                if (cityUrl) {
                  return (
                    <Link 
                      key={city.name}
                      to={cityUrl}
                      aria-label={`Elektrikář ${city.name}`}
                    >
                      <Badge 
                        variant="outline" 
                        className="px-3 py-1.5 text-sm bg-white/80 dark:bg-slate-800/80 border-amber-300 dark:border-amber-700 hover:bg-primary/20 hover:border-primary hover:scale-105 transition-all cursor-pointer group"
                      >
                        {city.name}
                        {city.distance && (
                          <span className="ml-1.5 text-xs text-muted-foreground">({city.distance} km)</span>
                        )}
                        <ArrowRight className="ml-1 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Badge>
                    </Link>
                  );
                }
                
                return (
                  <Badge 
                    key={city.name}
                    variant="outline" 
                    className="px-3 py-1.5 text-sm bg-white/80 dark:bg-slate-800/80 border-amber-300 dark:border-amber-700 hover:bg-primary/10 hover:border-primary transition-colors"
                  >
                    {city.name}
                    {city.distance && (
                      <span className="ml-1.5 text-xs text-muted-foreground">({city.distance} km)</span>
                    )}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Secondary Cities */}
          <div className="text-center mb-8">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">+ další města:</span>{" "}
              {SERVICE_AREA.secondaryCities.map(c => c.name).join(", ")} a další do {SERVICE_AREA.radius} km
            </p>
            <p className="mt-2 text-sm font-medium text-primary">
              🌐 Celý {SERVICE_AREA.region}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+420732658712">
              <Button 
                variant="electric" 
                size="lg" 
                className="w-full sm:w-auto gap-2 shadow-lg shadow-primary/30"
              >
                <Phone className="w-5 h-5" />
                +420 732 658 712
              </Button>
            </a>
            <Link to="/kontakt">
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto gap-2 border-amber-400 hover:bg-amber-50 dark:border-amber-600 dark:hover:bg-amber-950/30"
              >
                <Mail className="w-5 h-5" />
                Napsat poptávku
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalServiceBanner;
